import { AttorneyLeadStatus } from "../../../../api";
import { HelpersService } from "../../../../services/Helpers";

/** Stages shown in filter chips / dropdowns, in pipeline order */
export const ATTORNEY_STAGE_FILTER_ORDER: AttorneyLeadStatus[] = [
  AttorneyLeadStatus.NEW,
  AttorneyLeadStatus.ACCEPTED,
  AttorneyLeadStatus.INTRODUCTION,
  AttorneyLeadStatus.REJECTED,
  AttorneyLeadStatus.LOST,
  AttorneyLeadStatus.WON,
  AttorneyLeadStatus.SIGNED,
  AttorneyLeadStatus.PAID,
];

export function formatAttorneyStageFilterCount(
  status: AttorneyLeadStatus,
  count: number
): string {
  return `${getAttorneyStageFilterLabel(status)}(${count})`;
}

const attorneyStageCountKeys: Record<AttorneyLeadStatus, string> = {
  [AttorneyLeadStatus.NEW]: "new",
  [AttorneyLeadStatus.ACCEPTED]: "accepted",
  [AttorneyLeadStatus.INTRODUCTION]: "introduction",
  [AttorneyLeadStatus.REJECTED]: "rejected",
  [AttorneyLeadStatus.LOST]: "lost",
  [AttorneyLeadStatus.WON]: "won",
  [AttorneyLeadStatus.SIGNED]: "signed",
  [AttorneyLeadStatus.PAID]: "paid",
};

export function getAttorneyStageCount(
  status: AttorneyLeadStatus,
  counts?: Record<string, number> | null
): number {
  if (!counts) {
    return 0;
  }
  const key = attorneyStageCountKeys[status];
  return counts[key] ?? 0;
}

/** Short label for filter chips and table stage column */
export const AttorneyStageFilterLabels: Record<AttorneyLeadStatus, string> = {
  [AttorneyLeadStatus.NEW]: "New",
  [AttorneyLeadStatus.ACCEPTED]: "Accepted",
  [AttorneyLeadStatus.REJECTED]: "Rejected",
  [AttorneyLeadStatus.INTRODUCTION]: "Intake scheduled",
  [AttorneyLeadStatus.LOST]: "Opportunity lost",
  [AttorneyLeadStatus.WON]: "Opportunity won",
  [AttorneyLeadStatus.SIGNED]: "Agreement signed",
  [AttorneyLeadStatus.PAID]: "Paid",
};

/**
 * Human-readable attorney stage for tables and lead headers.
 * "Signed" here means the referral agreement was signed — not the same as "Accepted".
 */
export function getAttorneyStageLabel(
  status?: AttorneyLeadStatus | null,
  withoutPayment?: boolean | null
): string {
  if (!status) {
    return "—";
  }

  const base =
    AttorneyStageFilterLabels[status] ??
    HelpersService.camelToString(status, false);

  if (withoutPayment === true) {
    return base;
  }

  switch (status) {
    case AttorneyLeadStatus.ACCEPTED:
      return `${base} · awaiting GLio`;
    case AttorneyLeadStatus.INTRODUCTION:
      return `${base} · awaiting intake`;
    case AttorneyLeadStatus.WON:
      return `${base} · sign agreement`;
    case AttorneyLeadStatus.SIGNED:
      return `${base} · pay fee`;
    default:
      return base;
  }
}

export function getAttorneyStageFilterLabel(
  status: AttorneyLeadStatus | null
): string {
  if (status === null) {
    return "All";
  }
  return AttorneyStageFilterLabels[status] ?? HelpersService.camelToString(status);
}

/** Admin referral pipeline — distinct from the attorney's personal stage */
export function getReferralPipelineLabel(adminStatus?: string | null): string {
  if (!adminStatus) {
    return "—";
  }
  return `Referral: ${HelpersService.camelToString(adminStatus)}`;
}
